import { createApp } from 'vue'
import App from './App.vue'
import i18n from './i18n'
import router from './router'
import store from "./store"
import axios from 'axios'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import * as styles from 'vuetify/styles'
import '@mdi/font/scss/materialdesignicons.scss'
import vClickOutside from "click-outside-vue3"
import VueGtag from 'vue-gtag'
import Hotjar from 'vue-hotjar'
import { createGtm } from '@gtm-support/vue-gtm'
import { captureConsoleIntegration } from "@sentry/integrations"
import * as Sentry from '@sentry/vue'
import Keycloak from 'keycloak-js'

const gtm = createGtm({
  id:  process.env.VUE_APP_ENV == 'prod' ? 'GTM-5PSDFF7' : 'GTM-T5TVQT8Z',
  enabled: true,
})

const invictaLightTheme = {
  dark: false,
  colors: {
    background: '#FFFFFF',
    secondarybg: '#E3ECF8',
    secondary300: '#E3ECF8',
    surface: '#FFFFFF',
    primary: '#00205B',
    primary200: '#96A3BA',
    'on-primary': '#EEF4FE',
    'primary-darken-1': '#3700B3',
    secondary: '#EEF4FE',
    'secondary-darken-1': '#018786',
    error: '#C72323',
    info: '#2196F3',
    success: '#4CAF50',
    warning: '#FB8C00',
    yellow: '#ffe664',
    darkblue: '201E4C',
    lightblue: '#EEF4FE',
    lightgrey: '#E5EBF3',
    grey: '#9E9E9E',
    green: '#6BD783',
    red: '#E84545'
  }
}

const vuetify = createVuetify({
  components,
  directives,
  styles,
  theme: {
    defaultTheme: 'invictaLightTheme',
    themes: {
      invictaLightTheme,
    }
  },
  defaults: {
    global: {
      ripple: false,
    }
  },
  display: {
    mobileBreakpoint: 'md'
  }
})

axios.defaults.baseURL = process.env.VUE_APP_SBO_BACKEND_URL;
axios.defaults.headers.common['Accept-Language'] = process.env.VUE_APP_I18N_LOCALE;
axios.interceptors.request.use(
  async (config) => {
    const token = store.state.oidcStore.access_token
    if (token) {

      //refresh access token if expired or will expire in at least 60 seconds
      let needToRefreshAccessToken = Date.now() > (store.state.oidcStore.expires_at-60*1000)

      if(needToRefreshAccessToken) {
        await store.dispatch('oidcStore/authenticateOidcSilent')
        .then((context) => {
            const accessToken = context.access_token
            if (accessToken) {
              config.headers.Authorization = `Bearer ${accessToken}`
            }
          }
        )
      } else {
        config.headers.Authorization = `Bearer ${token}`
      }

    }
    return config
  }
)

const app = createApp(App)

Sentry.init({
  app,
  dns: process.env.VUE_APP_GLITCHTIP_DNS,
  integrations: [captureConsoleIntegration()],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0
});

const keycloak = new Keycloak({
  url: process.env.VUE_APP_AUTH_SERVER_URL,
  realm: process.env.VUE_APP_AUTH_REALM_NAME,
  clientId: process.env.VUE_APP_CLIENT_ID
});

app.config.globalProperties.$keycloak = keycloak
app.config.globalProperties.$keycloakInitOptions = {
  onLoad: 'check-sso',
  silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
  checkLoginIframe: true
}

app.use(vuetify)
	.use(router)
  .use(store)
	.use(i18n)
  .use(vClickOutside)
  .use(gtm)
  .use(VueGtag, {
    config: {
      id: process.env.VUE_APP_GA_MEASUREMENT_ID
    }
  })
  .use (Hotjar, {
    id: process.env.VUE_APP_HOTJAR_ID
  })
  .mount("#app")